var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useEffect, useState } from "react";
import { Modal, ModalFooter, Button, Icon, Loading, AccordionSet, } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery } from "react-query";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useOkapiKy, useCallout } from "@folio/stripes/core";
import { fetchProductById, updateProduct } from "../../api/productService";
import { buildProductPayload } from "../../utils/buildProductPayload";
import { createProductSchema } from "../../utils/validationUtils";
import { useScrollToError } from "../../hooks/useScrollToError";
import { ProductGeneralSection } from "../product/ProductGeneralSection";
import { ProductMeasuresSection } from "../product/ProductMeasuresSection";
import { ProductExtentsSection } from "../product/ProductExtentsSection";
import { ProductDetailsSection } from "../product/ProductDetailsSection";
import { ProductPriceSection } from "../product/ProductPriceSection";
import { ProductRelationsSection } from "../product/ProductRelationsSection";
/**
 * Modal for editing an existing product — the same form (all sections) as the
 * create modal, so editing happens in a dialog instead of a separate page.
 */
export var ProductEditModal = function (_a) {
    var productId = _a.productId, onClose = _a.onClose, onSaved = _a.onSaved, onError = _a.onError;
    var formatMessage = useIntl().formatMessage;
    var ky = useOkapiKy();
    var callout = useCallout();
    var _b = useState(false), showPrice = _b[0], setShowPrice = _b[1];
    var _c = useState({
        general: true,
        measures: true,
        extents: true,
        details: true,
        price: true,
        relations: true,
    }), accordionStatus = _c[0], setAccordionStatus = _c[1];
    var toggleAccordion = function (section) {
        return setAccordionStatus(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[section] = !prev[section], _a)));
        });
    };
    var _d = useQuery(["product", productId], function () { return fetchProductById(ky, productId); }, { enabled: !!productId, cacheTime: 0 }), product = _d.data, isLoading = _d.isLoading;
    var methods = useForm({
        resolver: zodResolver(createProductSchema(formatMessage)),
        mode: "onSubmit",
    });
    var handleSubmit = methods.handleSubmit, reset = methods.reset, errors = methods.formState.errors;
    useScrollToError(errors);
    // Seed the form once the product is loaded.
    useEffect(function () {
        if (product) {
            reset(product);
            setShowPrice(!!product.price);
        }
    }, [product, reset]);
    var saveMutation = useMutation(function (payload) { return updateProduct(ky, payload); }, {
        onSuccess: function () {
            onSaved(productId);
            callout === null || callout === void 0 ? void 0 : callout.sendCallout({
                type: "success",
                message: formatMessage({
                    id: "ui-br-agency.title.products.panel.edit.success",
                }),
            });
            onClose();
        },
        onError: function () {
            onError(formatMessage({ id: "ui-br-agency.product.error.update" }));
        },
    });
    var onSubmit = function (data) {
        if (!product)
            return;
        saveMutation.mutate(
        // Keep id + version (carried in `data` from the loaded product) so the
        // optimistic-locking PUT succeeds; title comes from the loaded product.
        buildProductPayload(data, { title: product.title, showPrice: showPrice }));
    };
    var footer = (React.createElement(ModalFooter, null,
        React.createElement(Button, { buttonStyle: "primary", disabled: saveMutation.isLoading || isLoading || !product, onClick: handleSubmit(onSubmit) }, saveMutation.isLoading ? (React.createElement(React.Fragment, null,
            React.createElement(Icon, { icon: "spinner-ellipsis", size: "small" }),
            React.createElement(FormattedMessage, { id: "ui-br-agency.button.saving" }))) : (React.createElement(FormattedMessage, { id: "ui-br-agency.button.save" }))),
        React.createElement(Button, { onClick: onClose, disabled: saveMutation.isLoading },
            React.createElement(FormattedMessage, { id: "ui-br-agency.button.cancel" }))));
    return (React.createElement(Modal, { open: true, dismissible: true, onClose: onClose, size: "large", label: formatMessage({ id: "ui-br-agency.product.edit.pane.title" }), footer: footer }, isLoading || !product ? (React.createElement(Loading, null)) : (React.createElement(FormProvider, __assign({}, methods),
        React.createElement("form", { noValidate: true, onSubmit: handleSubmit(onSubmit) },
            React.createElement(AccordionSet, null,
                React.createElement(ProductGeneralSection, { open: accordionStatus.general, onToggle: function () { return toggleAccordion("general"); } }),
                React.createElement(ProductMeasuresSection, { open: accordionStatus.measures, onToggle: function () { return toggleAccordion("measures"); } }),
                React.createElement(ProductExtentsSection, { open: accordionStatus.extents, onToggle: function () { return toggleAccordion("extents"); } }),
                React.createElement(ProductDetailsSection, { open: accordionStatus.details, onToggle: function () { return toggleAccordion("details"); } }),
                React.createElement(ProductPriceSection, { showPrice: showPrice, onShowPriceChange: setShowPrice, open: accordionStatus.price, onToggle: function () { return toggleAccordion("price"); } }),
                React.createElement(ProductRelationsSection, { open: accordionStatus.relations, onToggle: function () { return toggleAccordion("relations"); } })))))));
};
